import { useEffect, useState } from "react";
import { AgreeTerms } from "@/components/asv/AgreeTerms";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { usePlayer } from "@/lib/asv";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { recaptchaToken, useRecaptchaPreload } from "@/lib/recaptcha";
import { ForgotForm, ResetForm } from "@/components/asv/PasswordForms";

export default function AsvAuth() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = usePlayer();
  const { login, user } = useAuth();
  useEffect(() => { if (isAdmin(user) && !sp.get("reset")) nav("/platform", { replace: true }); }, [user, sp, nav]);
  const staffLogin = async (playerErr) => {
    try { await login(f.email, f.password); } catch { throw playerErr; }
    nav("/platform");
  };
  const [mode, setMode] = useState(sp.get("reset") ? "reset" : sp.get("mode") === "signup" ? "signup" : "signin");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useRecaptchaPreload(mode === "signup" ? "register" : "login", "asv");
  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !agree) { toast.error("Please agree to the Terms of Use and Privacy Policy"); return; }
    setBusy(true);
    try {
      try {
        const recaptcha_token = await recaptchaToken(mode === "signup" ? "register" : "login", "asv");
        await api.post(mode === "signup" ? "/asv/auth/register" : "/asv/auth/login", mode === "signup" ? { ...f, recaptcha_token, agree_terms: agree } : { email: f.email, password: f.password, recaptcha_token });
      } catch (err) {
        if (mode === "signup" || ![401, 429].includes(err.response?.status)) throw err;
        return await staffLogin(err);
      }
      await refresh();
      nav(sp.get("next") || "/account");
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  const title = { signup: "Create your account", forgot: "Forgot password", reset: "Set a new password" }[mode] || "Sign in";
  return (
    <div className="mx-auto max-w-md py-16" data-testid="asv-auth-page">
      <h1 className="asv-display text-center text-4xl font-extrabold" data-testid="auth-title">{title}</h1>
      {mode === "forgot" && <ForgotForm onBack={() => setMode("signin")} />}
      {mode === "reset" && <ResetForm token={sp.get("reset")} />}
      {["signin", "signup"].includes(mode) && <form onSubmit={submit} className="asv-card mt-8 space-y-3 p-6">
        {mode === "signup" && <><input className="asv-input" placeholder="Full name" value={f.name} onChange={s("name")} required data-testid="auth-name" />
          <input className="asv-input" placeholder="Phone" value={f.phone} onChange={s("phone")} data-testid="auth-phone" /></>}
        <input type="email" className="asv-input" placeholder="Email" value={f.email} onChange={s("email")} required data-testid="auth-email" />
        <input type="password" className="asv-input" placeholder="Password (min 8)" value={f.password} onChange={s("password")} required minLength={mode === "signup" ? 8 : 1} data-testid="auth-password" />
        {mode === "signin" && <div className="text-right"><button type="button" className="text-sm underline hover:opacity-80" onClick={() => setMode("forgot")} data-testid="auth-forgot-link">Forgot password?</button></div>}
        {mode === "signup" && <AgreeTerms checked={agree} onChange={setAgree} testid="auth-agree-terms" />}
        <button className="asv-btn w-full" disabled={busy || (mode === "signup" && !agree)} data-testid="auth-submit">{mode === "signup" ? "Sign up" : "Sign in"}</button>
        <button type="button" className="asv-muted w-full text-sm" onClick={() => setMode(mode === "signup" ? "signin" : "signup")} data-testid="auth-switch">{mode === "signup" ? "Have an account? Sign in" : "New here? Create an account"}</button>
      </form>}
      <p className="asv-muted mt-6 text-center text-xs">Own a venue? <Link to="/join/vendor" className="underline">List it on All Sports Venues</Link></p>
    </div>
  );
}
